import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { CONTENT_KEYS, SITE_APPEARANCE_DEFAULT, SITE_CONTROLS_DEFAULT, resolveAppearance, type SiteAppearanceContent, type SiteControlsContent } from '../content/schema';
import { useContent } from './useContent';

const STORAGE_KEY = 'subfocus-site:appearance';
const CHOICE_KEY = 'subfocus-site:my-look';

function remembered(): SiteAppearanceContent {
  try {
    return resolveAppearance(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null'));
  } catch {
    return SITE_APPEARANCE_DEFAULT;
  }
}

function readChoice(): Partial<SiteAppearanceContent> {
  try {
    return JSON.parse(localStorage.getItem(CHOICE_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

export interface SiteAppearance extends SiteAppearanceContent {
  /** Tries a look in this browser only. Everyone else keeps the one set in admin. */
  choose(next: Partial<SiteAppearanceContent>): void;
}

/**
 * The look set in admin, for every public page, unless this browser has picked its own from the
 * view controls. Until the answer arrives this uses what the browser saw last time, so a returning
 * visitor does not see the wrong colours first.
 */
export function useSiteAppearance(): SiteAppearance {
  const { data, loading } = useContent<SiteAppearanceContent>(CONTENT_KEYS.siteAppearance, SITE_APPEARANCE_DEFAULT);
  const { data: controls } = useContent<Partial<SiteControlsContent>>(CONTENT_KEYS.siteControls, SITE_CONTROLS_DEFAULT);
  const [choice, setChoice] = useState(readChoice);
  // A choice only counts while admin still offers that control.
  const allowed = useMemo(() => ({
    ...(controls.enabled !== false && controls.background !== false && choice.backdrop ? { backdrop: choice.backdrop } : {}),
    ...(controls.enabled !== false && controls.theme !== false && choice.theme ? { theme: choice.theme } : {}),
  }), [controls.enabled, controls.background, controls.theme, choice]);
  const appearance = useMemo(() => resolveAppearance({ ...(loading ? remembered() : resolveAppearance(data)), ...allowed }), [loading, data, allowed]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', appearance.theme);
    if (loading) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
    } catch {
      /* Remembering is a convenience, and private browsers may refuse. */
    }
  }, [appearance, loading]);

  const choose = useCallback((next: Partial<SiteAppearanceContent>) => {
    const merged = { ...choice, ...next };
    setChoice(merged);
    try {
      localStorage.setItem(CHOICE_KEY, JSON.stringify(merged));
    } catch {
      /* The choice still applies until the page reloads. */
    }
  }, [choice]);

  return useMemo(() => ({ ...appearance, choose }), [appearance, choose]);
}
